import { useState } from "react";
import ModalComponent from "../../../../components/modals/ModalFormComponent";
import Label from "../../../../components/form_fields/Label";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  useCreateSavingsScheme,
  useUpdateSavingsScheme,
} from "../../../../services/savings-scheme.service";
import { APPLY_SAVINGS_CONTRIBUTION_ON } from "../../../../models/savings-scheme.dto";
import {
  enumToStringConverter,
  formatRate,
  IAddEditTableForm,
  manageResourceIds,
} from "../../../../helpers/appHelpers";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { EmployeeBandDto } from "../../../../models/employee-band.dto";
import { EmployeeDto } from "../../../../models/employee.dto";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import APISelectField from "../../../../components/form_fields/APISelectField";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import MultiSelectField from "../../../../components/form_fields/MultiSelectField";
import { PATHS } from "../../../../routes/routes.paths";
import RateComponent from "../../../../components/form_fields/RateComponent";
import { TaxReliefTypeDto } from "../../../../models/tax-relief-type.dto";

interface IFormInputs {
  name: string;
  code: string;
  description: string;
  startPayPeriodId?: number;
  endPayPeriodId?: number;
  employeeIds?: number[];
  employeeBandIds?: number[];
  applyEmployeeContributionOn: APPLY_SAVINGS_CONTRIBUTION_ON;
  applyEmployerContributionOn: APPLY_SAVINGS_CONTRIBUTION_ON;
  employeeContributionFigure: number;
  employerContributionFigure: number;
  prorate: string;
  statutory: string;
  taxReliefTypeId?: number;
}

type SavingsModalFormProps = IAddEditTableForm & {
  payPeriods: PayPeriodDto[];
  employees: EmployeeDto[];
  employeeBands: EmployeeBandDto[];
  taxReliefTypes: TaxReliefTypeDto[];
  reloadTableData?: () => void;
};

const SavingsModalForm = ({
  open,
  setOpen,
  title,
  isEditForm,
  tableAPIRef,
  payPeriods,
  employees,
  employeeBands,
  taxReliefTypes,
  reloadTableData,
}: SavingsModalFormProps) => {
  const { currentCompany } = useCompanyStore();
  const { formDataValues } = useEditFormStore();
  const [uploading, setUploading] = useState(false);

  const [employeeFixedComponent, setEmployeeFixedComponent] = useState(
    (isEditForm &&
      formDataValues?.applyEmployeeContributionOn.toLowerCase() === "fixed") ||
      false,
  );
  const [employerFixedComponent, setEmployerFixedComponent] = useState(
    (isEditForm &&
      formDataValues?.applyEmployerContributionOn.toLowerCase() === "fixed") ||
      false,
  );

  const handleFixedComponent = (value: string, employee: boolean) => {
    if (value.toLowerCase() === "fixed") {
      employee
        ? setEmployeeFixedComponent(true)
        : setEmployerFixedComponent(true);
    } else {
      employee
        ? setEmployeeFixedComponent(false)
        : setEmployerFixedComponent(false);
    }
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState: { errors },
  } = useForm<IFormInputs>({
    defaultValues: {
      name: isEditForm ? formDataValues?.name : "",
      code: isEditForm ? formDataValues?.code : "",
      description: isEditForm ? formDataValues?.description : "",
      startPayPeriodId: isEditForm ? formDataValues?.startPayPeriodId : "",
      endPayPeriodId: isEditForm ? formDataValues?.endPayPeriodId : "",
      applyEmployeeContributionOn: isEditForm
        ? formDataValues?.applyEmployeeContributionOn
        : "",
      applyEmployerContributionOn: isEditForm
        ? formDataValues?.applyEmployerContributionOn
        : "",
      taxReliefTypeId: isEditForm ? formDataValues?.taxReliefTypeId : "",
      prorate: isEditForm ? (formDataValues?.prorate ? "yes" : "no") : "",
      statutory: isEditForm ? (formDataValues?.statutory ? "yes" : "no") : "",
    },
  });

  // Seeded directly from formDataValues at declaration time (edit mode),
  // rather than starting empty and relying on MultiSelectField's
  // `defaultValues` prop to sync it up later. That prop only pre-checks the
  // field's own display state — it never calls setResourceIds itself — so
  // if the user never touches a field on edit, these would otherwise stay
  // [] while the scheme already has N employees/bands attached, and
  // manageResourceIds would read that as "remove all N" on submit.
  const [employeeIds, setEmployeeIds] = useState<number[]>(() =>
    isEditForm
      ? formDataValues?.employeeSavingsSchemes?.map(
          (employeeSavingsScheme: any) => employeeSavingsScheme?.employee.id,
        ) || []
      : [],
  );
  const [employeeBandIds, setEmployeeBandIds] = useState<number[]>(() =>
    isEditForm
      ? formDataValues?.employeeBandSavingsSchemes?.map(
          (employeeBandSavingsScheme: any) =>
            employeeBandSavingsScheme?.employeeBand.id,
        ) || []
      : [],
  );
  const { mutate: createSavings } = useCreateSavingsScheme();
  const { mutate: updateSavings } = useUpdateSavingsScheme();
  const { addNewTableData } = useManageTableWithSearchParamsData();

  const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
    // Validate percentage contribution fields before submission.
    // RateComponent stores values as the raw percentage (e.g. 55 for 55%);
    // formatRate then converts them to decimals (0.55) for the API.
    // Fixed-amount fields use MoneyInput and have no upper bound, so only
    // validate when the field is in percentage (non-fixed) mode.
    if (!employeeFixedComponent) {
      const employeeValue = Number(data.employeeContributionFigure);
      if (employeeValue > 100) {
        setError("employeeContributionFigure", {
          type: "manual",
          message: "Employee contribution cannot exceed 100%",
        });
        return;
      }
      if (employeeValue < 0) {
        setError("employeeContributionFigure", {
          type: "manual",
          message: "Employee contribution cannot be negative",
        });
        return;
      }
    }

    if (!employerFixedComponent) {
      const employerValue = Number(data.employerContributionFigure);
      if (employerValue > 100) {
        setError("employerContributionFigure", {
          type: "manual",
          message: "Employer contribution cannot exceed 100%",
        });
        return;
      }
      if (employerValue < 0) {
        setError("employerContributionFigure", {
          type: "manual",
          message: "Employer contribution cannot be negative",
        });
        return;
      }
    }

    setUploading(true);

    !employeeFixedComponent && formatRate(data, "employeeContributionFigure");
    !employerFixedComponent && formatRate(data, "employerContributionFigure");
    data.prorate = data.prorate === "yes" ? true : false;
    data.statutory = data.statutory === "yes" ? true : false;

    if (isEditForm) {
      const { addIds: addEmployeeIds, removeIds: removeEmployeeIds } =
        manageResourceIds({
          newData: employeeIds,
          oldData:
            formDataValues?.employeeSavingsSchemes?.map(
              (employeeSavingsScheme) => employeeSavingsScheme?.employee.id,
            ) || [],
        });
      const { addIds: addEmployeeBandIds, removeIds: removeEmployeeBandIds } =
        manageResourceIds({
          newData: employeeBandIds,
          oldData:
            formDataValues?.employeeBandSavingsSchemes?.map(
              (employeeBandSavingsScheme) =>
                employeeBandSavingsScheme?.employeeBand.id,
            ) || [],
        });

      data.addEmployeeIds = addEmployeeIds;
      data.removeEmployeeIds = removeEmployeeIds;
      data.addEmployeeBandIds = addEmployeeBandIds;
      data.removeEmployeeBandIds = removeEmployeeBandIds;

      updateSavings(
        { payload: data, id: formDataValues?.id },
        {
          onSuccess: (updatedData) => {
            try {
              tableAPIRef?.current.updateRows([updatedData.data]);
            } catch (error) {
              console.error("Error updating table rows:", error);
              reloadTableData?.();
            }
            setOpen(false);
            reset();
          },
          onSettled: () => setUploading(false),
        },
      );
    } else {
      data.employeeIds = employeeIds.length ? (employeeIds as any) : undefined;
      data.employeeBandIds = employeeBandIds.length
        ? (employeeBandIds as any)
        : undefined;

      createSavings(
        { payload: { ...data, companyId: currentCompany?.id as number } },
        {
          onSuccess: () => {
            addNewTableData();
            setOpen(false);
            reset();
          },
          onSettled: () => setUploading(false),
        },
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  return (
    <ModalComponent open={open} title={title} handleClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-2 gap-x-4 gap-y-6">
          <div className="w-full">
            <Label title="Name" for="name" />
            <input
              placeholder={"Enter name"}
              type="text"
              {...register("name", {
                required: "Enter name here",
              })}
              id="name"
              className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
                errors.name
                  ? " border border-red-500 focus:border-red-500 focus:outline-red-500"
                  : "border-none"
              }`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>
          <div className="w-full">
            <Label title="Unique Code" for="code" />
            <FormCodeInput
              register={register}
              attributes={{
                className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
                  errors.code
                    ? " border border-red-500 focus:border-red-500 focus:outline-red-500"
                    : "border-none"
                }`,
              }}
            />
            {errors.code && (
              <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
            )}
          </div>
          <div className="w-full col-span-2">
            <Label title="Description" for="description" />
            <FormDescriptionInput
              register={register}
              attributes={{
                defaultValue: isEditForm ? formDataValues?.description : "",
                className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
                  errors.description
                    ? " border border-red-500 focus:border-red-500 focus:outline-red-500"
                    : "border-none"
                }`,
              }}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Start Pay Period" for="startPayPeriodId" optional />
            <APISelectField
              key="startPayPeriodId"
              attributes={{
                id: "startPayPeriodId",
              }}
              placeholder="Select Pay Period"
              register={register}
              name="startPayPeriodId"
              options={payPeriods}
              mapText="code"
              pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
              required={false}
              error={errors.startPayPeriodId}
              valueAsNumber
            />
            {errors.startPayPeriodId && (
              <p className="text-red-500 text-sm mt-1">
                {errors.startPayPeriodId.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="End Pay Period" for="endPayPeriodId" optional />
            <APISelectField
              key="endPayPeriodId"
              attributes={{
                id: "endPayPeriodId",
              }}
              placeholder="Select Pay Period"
              register={register}
              name="endPayPeriodId"
              options={payPeriods}
              mapText="code"
              pathname={PATHS.SUB_COMPANY_SETTINGS.PAY_PERIODS}
              required={false}
              error={errors.endPayPeriodId}
              valueAsNumber
            />
            {errors.endPayPeriodId && (
              <p className="text-red-500 text-sm mt-1">
                {errors.endPayPeriodId.message}
              </p>
            )}
          </div>
          <div className="w-full">
            <Label title="Assign Employees" for="employeeIds" optional />
            <MultiSelectField
              key={"employeeIds"}
              options={employees}
              setResourceIds={setEmployeeIds}
              placeholder={"Select Employees"}
              pathname={PATHS.EMPLOYEES}
              required={false}
              mapTexts={["firstName", "lastName"]}
              defaultValues={
                !isEditForm
                  ? []
                  : formDataValues?.employeeSavingsSchemes?.map(
                      (employeeSavingsScheme) =>
                        employeeSavingsScheme?.employee.id,
                    ) || []
              }
            />
          </div>
          <div className="w-full">
            <Label
              title="Assign Employee Bands"
              for="employeeBandIds"
              optional
            />
            <MultiSelectField
              key={"employeeBandIds"}
              options={employeeBands}
              setResourceIds={setEmployeeBandIds}
              placeholder={"Select Bands"}
              pathname={PATHS.SUB_COMPANY_SETTINGS.BANDS}
              required={false}
              defaultValues={
                !isEditForm
                  ? []
                  : formDataValues?.employeeBandSavingsSchemes?.map(
                      (employeeBandSavingsScheme) =>
                        employeeBandSavingsScheme?.employeeBand.id,
                    ) || []
              }
            />
          </div>
          <div className="w-full">
            <Label title="Tax Relief Type" for="taxReliefTypeId" optional />
            <APISelectField
              key="taxReliefTypeId"
              attributes={{
                id: "taxReliefTypeId",
              }}
              placeholder="Select Type"
              register={register}
              name="taxReliefTypeId"
              options={taxReliefTypes}
              required={false}
              error={errors.taxReliefTypeId}
              valueAsNumber
              doNotLinkToResource
            />
          </div>
        </div>

        <div className="mt-6">
          <h3 className="font-semibold text-base mb-3">
            Employee Contribution
          </h3>

          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            <div className="w-full">
              <Label
                title="Apply Contribution On"
                for="applyEmployeeContributionOn"
              />
              <select
                defaultValue={
                  isEditForm ? formDataValues?.applyEmployeeContributionOn : ""
                }
                className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
                  errors.applyEmployeeContributionOn
                    ? " border border-red-500 focus:border-red-500 focus:outline-red-500"
                    : "border-none"
                }`}
                {...register("applyEmployeeContributionOn", {
                  required: "Select employee terms here",
                })}
                onChange={(e) => handleFixedComponent(e.target.value, true)}
              >
                <option value="">Select Terms</option>
                <option value="BASIC">Basic</option>
                <option value="GROSS">Gross</option>
                <option value="FIXED">Fixed</option>
              </select>
              {errors.applyEmployeeContributionOn && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.applyEmployeeContributionOn.message}
                </p>
              )}
            </div>
            <div className="w-full">
              <Label
                title="Employee Contribution Figure"
                for="employeeContributionFigure"
              />
              {!employeeFixedComponent ? (
                <RateComponent
                  attributes={{
                    id: "employeeContributionFigure",
                    defaultValue: isEditForm
                      ? Number(formDataValues?.employeeRateComponent) * 100
                      : undefined,
                  }}
                  register={register}
                  name="employeeContributionFigure"
                  required="Enter employee contribution figure"
                  error={errors.employeeContributionFigure}
                />
              ) : (
                <MoneyInput
                  attributes={{
                    id: "employeeContributionFigure",
                    placeholder: "0",
                  }}
                  register={register}
                  name="employeeContributionFigure"
                  setValue={setValue}
                  defaultValue={
                    isEditForm
                      ? formDataValues?.employeeFixedComponent
                      : undefined
                  }
                  required="Enter employee contribution figure"
                  error={errors.employeeContributionFigure}
                />
              )}
              {errors.employeeContributionFigure && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.employeeContributionFigure.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="font-semibold text-base mb-3">
            Employer Contribution
          </h3>

          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            <div className="w-full">
              <Label
                title="Apply Contribution On"
                for="applyEmployerContributionOn"
              />
              <select
                defaultValue={
                  isEditForm ? formDataValues?.applyEmployerContributionOn : ""
                }
                className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
                  errors.applyEmployerContributionOn
                    ? " border border-red-500 focus:border-red-500 focus:outline-red-500"
                    : "border-none"
                }`}
                {...register("applyEmployerContributionOn", {
                  required: "Select employer terms here",
                })}
                onChange={(e) => handleFixedComponent(e.target.value, false)}
              >
                <option value="">Select Terms</option>
                <option value="BASIC">Basic</option>
                <option value="GROSS">Gross</option>
                <option value="FIXED">Fixed</option>
              </select>
              {errors.applyEmployerContributionOn && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.applyEmployerContributionOn.message}
                </p>
              )}
            </div>
            <div className="w-full">
              <Label
                title="Employer Contribution Figure"
                for="employerContributionFigure"
              />
              {!employerFixedComponent ? (
                <RateComponent
                  attributes={{
                    id: "employerContributionFigure",
                    defaultValue: isEditForm
                      ? Number(formDataValues?.employerRateComponent) * 100
                      : undefined,
                  }}
                  register={register}
                  name="employerContributionFigure"
                  required="Enter employer contribution figure"
                  error={errors.employerContributionFigure}
                />
              ) : (
                <MoneyInput
                  attributes={{
                    id: "employerContributionFigure",
                    placeholder: "0",
                  }}
                  register={register}
                  name="employerContributionFigure"
                  setValue={setValue}
                  defaultValue={
                    isEditForm
                      ? formDataValues?.employerFixedComponent
                      : undefined
                  }
                  required="Enter employer contribution figure"
                  error={errors.employerContributionFigure}
                />
              )}
              {errors.employerContributionFigure && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.employerContributionFigure.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="flex space-x-12 text-base">
            <h3>Statutory</h3>
            <div className="flex space-x-3 items-center">
              <input
                defaultChecked={
                  isEditForm && formDataValues?.statutory === true
                }
                type="radio"
                value={"yes"}
                {...register("statutory", {
                  required: "Enter statutory here",
                })}
                id="statutory"
              />
              <label htmlFor="statutory">Yes</label>
            </div>
            <div className="flex space-x-3 items-center">
              <input
                defaultChecked={
                  isEditForm && formDataValues?.statutory === false
                }
                type="radio"
                value={"no"}
                {...register("statutory", {
                  required: "Enter statutory here",
                })}
                id="no-statutory"
              />
              <label htmlFor="no-statutory">No</label>
            </div>
          </div>
          {errors.statutory && (
            <p className="text-red-500 text-sm mt-1">
              {errors.statutory.message}
            </p>
          )}
        </div>
        <div className="mt-6">
          <div className="flex space-x-12 text-base">
            <h3>Prorate</h3>
            <div className="flex space-x-3 items-center">
              <input
                defaultChecked={isEditForm && formDataValues?.prorate === true}
                type="radio"
                value={"yes"}
                {...register("prorate", {
                  required: "Enter prorate here",
                })}
                id="prorate"
              />
              <label htmlFor="prorate">Yes</label>
            </div>
            <div className="flex space-x-3 items-center">
              <input
                defaultChecked={isEditForm && formDataValues?.prorate === false}
                type="radio"
                value={"no"}
                {...register("prorate", {
                  required: "Enter prorate here",
                })}
                id="no-prorate"
              />
              <label htmlFor="no-prorate">No</label>
            </div>
          </div>
          {errors.prorate && (
            <p className="text-red-500 text-sm mt-1">
              {errors.prorate.message}
            </p>
          )}
        </div>

        <div className="w-full flex justify-end mt-8">
          <OrangeButton
            type="submit"
            title={
              uploading
                ? "Uploading..."
                : isEditForm
                  ? "Update Savings Scheme"
                  : "Add Savings Scheme"
            }
            className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
            disabled={uploading}
          />
        </div>
      </form>
    </ModalComponent>
  );
};

export default SavingsModalForm;
